import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { GuestStatus } from 'types/guest.types';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { Badge } from '@epoch-ui/components/Badge';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import { Label } from '@epoch-ui/components/Form';
import { SearchInput } from '@epoch-ui/components/SearchInput/SearchInput';
import { inviteGuestsApi } from '@event-editor/api/invite-guests';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { useInviteGuestsModalContext } from '../../hooks/useInviteGuestsModalContext';

const MAX_DISPLAYED_RESULTS = 100;

export const InviteGuestSelector: React.FC = () => {
  const event = useEditableEvent();
  const { selectedUsers, toggleUser } = useInviteGuestsModalContext();

  const [searchText, setSearchText] = useState('');

  const { data: users = [], isLoading } = useQuery({
    queryKey: eventEditorQueryKeys.eventInvitableUsers(event.id!, searchText),
    queryFn: () =>
      inviteGuestsApi.getInvitableUsers({
        eventId: event.id!,
        searchText,
        perPage: MAX_DISPLAYED_RESULTS
      }),
    enabled: !!event.id
  });

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-mt-4">
        <SearchInput
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="tw-w-full"
        />
      </div>

      {isLoading ? (
        <div className="tw-flex tw-h-32 tw-items-center tw-justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="tw-relative tw-mt-2 tw-h-full tw-overflow-y-scroll tw-pr-1">
          <ul className="tw-mb-0 tw-max-h-[408px] tw-min-h-16 tw-list-none tw-p-0">
            {users.map((user) => {
              const selected = !!selectedUsers.find((u) => u.id === user.id);
              // Disable checkbox if user is already associated with event (except for UNINVITED status)
              const userHasStatus =
                user.currentStatus !== null && user.currentStatus !== GuestStatus.UNINVITED;

              return (
                <li className="tw-mx-0 tw-my-4 first:tw-mt-2" key={user.id}>
                  <div className="tw-flex tw-cursor-pointer tw-flex-row tw-items-center tw-justify-center">
                    <Checkbox
                      id={user.id}
                      checked={selected}
                      disabled={userHasStatus}
                      onCheckedChange={() => toggleUser(user)}
                    />

                    {/* Text section */}
                    <Label
                      htmlFor={user.id}
                      className={`tw-my-0 tw-ml-3 tw-mr-1 tw-flex-1 ${
                        userHasStatus ? 'tw-opacity-40' : 'tw-opacity-100'
                      }`}
                    >
                      <p className="tw-mb-0 tw-text-sm tw-leading-5 tw-text-black">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="tw-mb-0 tw-text-sm tw-leading-5 tw-text-primary-grey">
                        {user.email}
                      </p>
                    </Label>

                    {/* Already in guest list badge */}
                    {userHasStatus && <Badge variant="secondary">Already in guest list</Badge>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
